import { collection, doc, getDoc, getDocs, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { BookingStatus } from '../utils/dashboard/types';
import { resolveEncounterType } from '../utils/slotTypes';

export interface ClinicalHistorySlot {
  id: string;
  startAt: { toDate: () => Date };
  status: string;
  doctorName?: string;
  encounterType?: string | null;
  serviceType?: string | null;
}

export async function listAvailableClinicalHistorySlots(): Promise<ClinicalHistorySlot[]> {
  const slotsQuery = query(
    collection(db, 'clinical_history_slots'),
    where('status', '==', 'available'),
    where('startAt', '>=', Timestamp.fromDate(new Date())),
    orderBy('startAt', 'asc'),
  );

  const snapshot = await getDocs(slotsQuery);

  const slots = snapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
    startAt: docSnapshot.data().startAt,
  })) as ClinicalHistorySlot[];

  // Intake interviews only. Care Clinicians and seniors publish into the same
  // collection, so without this a member booking their clinical history could
  // land on a senior's quarterly-review slot. Filtered client-side to keep
  // resolveEncounterType's deny-by-default handling of legacy untyped slots,
  // and to avoid a composite index on an already-bounded query.
  return slots.filter((slot) => resolveEncounterType(slot) === 'intake_interview');
}

/** Re-exported so callers outside the dashboard keep one import site. */
export type ClinicalHistoryBookingStatus = BookingStatus;

export interface ClinicalHistoryBookingView {
  status: ClinicalHistoryBookingStatus;
  bookingId: string | null;
  slotId: string | null;
  /** When the interview actually is — resolved from the booked slot. */
  startAt: Date | null;
  endAt: Date | null;
  doctorName: string | null;
  meetingLink: string | null;
}

/** Collapse stored status values, including the legacy 'booked'. */
function normaliseBookingStatus(raw: unknown): ClinicalHistoryBookingStatus {
  const value = String(raw || 'pending');
  if (value === 'confirmed' || value === 'booked') return 'confirmed';
  if (value === 'cancelled') return 'cancelled';
  return 'pending';
}

export async function getMemberClinicalHistoryBooking(userId: string): Promise<ClinicalHistoryBookingView> {
  const bookingsQuery = query(
    collection(db, 'clinical_history_bookings'),
    where('userId', '==', userId),
  );
  const snapshot = await getDocs(bookingsQuery);
  if (snapshot.empty) {
    return { status: 'none', bookingId: null, slotId: null, startAt: null, endAt: null, doctorName: null, meetingLink: null };
  }

  const byNewest = snapshot.docs
    .map((docSnap) => ({ id: docSnap.id, data: docSnap.data() as Record<string, any> }))
    .sort((left, right) => {
      const leftMs = left.data.createdAt?.toMillis?.() || 0;
      const rightMs = right.data.createdAt?.toMillis?.() || 0;
      return rightMs - leftMs;
    });

  // Prefer the live booking, so cancel-then-rebook shows the new appointment
  // while a cancel-only still reports the cancelled state rather than nothing.
  //
  // AMONG SEVERAL LIVE BOOKINGS, THE SOONEST WINS — not the newest. A member can
  // now hold two: a clinician arranging a follow-up creates a second while the
  // first is still ahead of them. Ranking by createdAt showed the follow-up,
  // which is usually the one FURTHER away, so the member's dashboard would name
  // an appointment in three weeks while the one on Thursday went unmentioned.
  // Newest-first survives as the tiebreak for bookings with no time on them.
  const now = Date.now();
  const live = byNewest.filter((b) => normaliseBookingStatus(b.data.status) !== 'cancelled');
  const startMs = (b: { data: Record<string, any> }) => b.data.startAt?.toMillis?.() ?? null;
  const upcoming = live
    .filter((b) => (startMs(b) ?? -1) >= now)
    .sort((left, right) => (startMs(left) as number) - (startMs(right) as number));

  const chosen = upcoming[0] || live[0] || byNewest[0];

  const status = normaliseBookingStatus(chosen.data.status);
  const slotId = chosen.data.slotId ? String(chosen.data.slotId) : null;
  const denormalisedStart = chosen.data.startAt?.toDate?.() || null;
  const denormalisedDoctor = chosen.data.doctorName ? String(chosen.data.doctorName) : null;
  const denormalisedEnd = chosen.data.endAt?.toDate?.() || null;
  const denormalisedLink = chosen.data.meetingLink ? String(chosen.data.meetingLink) : null;

  // Bookings now carry the time themselves, so the dashboard no longer depends
  // on the slot join. That join was the single point of failure behind
  // "Nothing scheduled": a denied read, or a slot ops deleted after booking,
  // produced startAt: null inside a bare catch and the appointment silently
  // vanished. Older bookings still fall back to the join.
  if (denormalisedStart) {
    return {
      status, bookingId: chosen.id, slotId,
      startAt: denormalisedStart, endAt: denormalisedEnd,
      doctorName: denormalisedDoctor, meetingLink: denormalisedLink,
    };
  }
  if (!slotId) {
    return {
      status, bookingId: chosen.id, slotId: null,
      startAt: null, endAt: null, doctorName: denormalisedDoctor, meetingLink: denormalisedLink,
    };
  }

  try {
    const slotSnap = await getDoc(doc(db, 'clinical_history_slots', slotId));
    const slot = slotSnap.exists() ? slotSnap.data() : null;
    if (!slot) {
      console.warn(`clinical-history booking ${chosen.id} points at missing slot ${slotId}`);
    }
    return {
      status,
      bookingId: chosen.id,
      slotId,
      startAt: slot?.startAt?.toDate?.() || null,
      endAt: slot?.endAt?.toDate?.() || null,
      doctorName: slot?.doctorName ? String(slot.doctorName) : denormalisedDoctor,
      meetingLink: slot?.meetingLink ? String(slot.meetingLink) : denormalisedLink,
    };
  } catch (error) {
    // Never swallow this silently — it renders as "Nothing scheduled".
    console.warn(`could not read slot ${slotId} for booking ${chosen.id}`, error);
    return {
      status, bookingId: chosen.id, slotId,
      startAt: null, endAt: null, doctorName: denormalisedDoctor, meetingLink: denormalisedLink,
    };
  }
}

/**
 * Status only. Kept so existing callers (Home, the booking page) don't move;
 * delegates so there is one definition of how a raw status collapses.
 */
export async function getMemberClinicalHistoryBookingStatus(userId: string): Promise<ClinicalHistoryBookingStatus> {
  const booking = await getMemberClinicalHistoryBooking(userId);
  return booking.status;
}

export class SlotUnavailableError extends Error {}

/**
 * Book a published slot.
 *
 * Confirmed immediately — the clinician published the time, so there was never
 * anything to approve, and the old `pending` state left the member waiting on
 * a button click.
 *
 * Goes through a callable rather than writing directly because booking flips
 * two documents in two collections (the booking is created, the slot is marked
 * `booked`) and that must be atomic. Firestore rules evaluate each document
 * independently, so a client-side version would need every member to hold
 * write access to every slot's status.
 *
 * Throws SlotUnavailableError when someone took the time first — now possible,
 * since the approval step used to mask that race.
 */
export async function createClinicalHistoryBooking(input: {
  slotId: string;
}): Promise<{ bookingId: string; conversationId: string }> {
  const callable = httpsCallable<
    { slotId: string },
    { ok: boolean; bookingId: string; conversationId: string }
  >(functions, 'bookClinicalHistorySlot');
  try {
    const result = await callable({ slotId: input.slotId });
    return { bookingId: result.data.bookingId, conversationId: result.data.conversationId };
  } catch (error: any) {
    if (error?.code === 'functions/failed-precondition' || error?.code === 'functions/not-found') {
      throw new SlotUnavailableError(error?.message || 'That time is no longer available');
    }
    throw error;
  }
}

/** Cancel a booking. The caller may be the member who owns it, or staff. */
export async function cancelClinicalHistoryBooking(bookingId: string): Promise<void> {
  const callable = httpsCallable<{ bookingId: string }, { ok: boolean }>(
    functions,
    'cancelClinicalHistoryBooking',
  );
  await callable({ bookingId });
}
